import { useDocumentsUiStore } from '../extensions/bundled/documents/documentsUiStore';
import { docWorkspaceKey, normalizeWorkspaceKey } from './docWorkspaceKey';
import { setActiveDocumentTabId, setTabStripOrder, useTabsStore } from './tabsStore';

/**
 * Open document tabs per workspace (owner, 14.10.2026). Every document tab
 * stays in the one `tabsStore.documentTabs` list; a tab BELONGS to the
 * workspace its scope names (`personal` | `personal/<scopeId>` | `device` |
 * `team:<id>` — the sidebar switcher's keys), and the strip only ever renders
 * the tabs of the workspace the switcher shows. Switching is therefore one
 * store write and one render: nothing is unmounted into a hidden list, nothing
 * is fetched, nothing fades.
 *
 * Two invariants, kept by two store subscriptions (installed once):
 *
 * 1. The switcher moves → the active document tab becomes the one that was
 *    active in that workspace last (or its right-most tab, or none: the
 *    workspace's empty state).
 * 2. A document tab of another workspace is ACTIVATED → the switcher follows
 *    it. That one rule covers every "open a document from elsewhere" path
 *    (search, palette, links, MCP reveal, Graph).
 *
 * A move of the document on screen to another workspace takes its tab along:
 * the tab joins that workspace's set (as its active tab) and the workspace in
 * view shows its next tab — see onTabsChange.
 *
 * No React in here except the one selector hook.
 */

export { docWorkspaceKey, normalizeWorkspaceKey };

export const tabWorkspaceKey = (tab) => docWorkspaceKey(tab?.scope);

/** The workspace the Documents sidebar shows now. `getState?.` — some tests stub the store as a bare hook. */
export function currentDocWorkspaceKey() {
  return normalizeWorkspaceKey(useDocumentsUiStore.getState?.()?.scopeKey);
}

/** React: the current workspace key (re-renders on a switch). */
export function useDocWorkspaceKey() {
  return normalizeWorkspaceKey(useDocumentsUiStore((s) => s.scopeKey));
}

/** The tabs of `key`, in their given order. */
export function tabsOfWorkspace(tabs, key = currentDocWorkspaceKey()) {
  return (tabs || []).filter((tab) => tabWorkspaceKey(tab) === key);
}

/** The document tabs of `key` in strip order (the order the user sees). */
export function orderedWorkspaceTabs(state, key) {
  const mine = tabsOfWorkspace(state.documentTabs, key);
  const pos = new Map();
  (state.tabStripOrder || []).forEach((e, i) => {
    if (e.type === 'document-editor') pos.set(e.id, i);
  });
  return [...mine].sort((a, b) => (pos.get(a.id) ?? Infinity) - (pos.get(b.id) ?? Infinity));
}

// ── the per-workspace "last active tab" map ─────────────────────────────────

let lastActive = {};

export function getLastActiveMap() {
  return { ...lastActive };
}

export function setLastActiveMap(map) {
  lastActive = map && typeof map === 'object' ? { ...map } : {};
}

/**
 * The tab to show when `key` comes on screen: the one active there last, else
 * the right-most tab of that workspace, else null (its empty state).
 */
export function tabToShowFor(state, key) {
  const mine = orderedWorkspaceTabs(state, key);
  const remembered = mine.find((tab) => tab.id === lastActive[key]);
  if (remembered) return remembered.id;
  return mine.length ? mine[mine.length - 1].id : null;
}

/**
 * After closing tabs: which tab of the CURRENT workspace takes over (the
 * strip's neighbour rule — the right-most remaining one), never a tab of
 * another workspace, which would switch the sidebar away under the user.
 */
export function documentCloseFallback(remainingTabs) {
  const mine = tabsOfWorkspace(remainingTabs);
  return mine.length ? mine[mine.length - 1].id : null;
}

// ── the two invariants ──────────────────────────────────────────────────────

let installed = false;

function activeTab(state) {
  return state.documentTabs.find((tab) => tab.id === state.activeDocumentTabId) || null;
}

/**
 * Tabs re-pointed with `followDocumentTab` (a correction of where the document
 * always was, not a move by the user): the view follows them.
 */
const following = new Set();

/** Re-point a tab's scope as a CORRECTION: if it is on screen, the sidebar follows it. */
export function followDocumentTab(documentId, scope, update) {
  following.add(`document-editor:${documentId}`);
  try {
    update(documentId, scope);
  } finally {
    following.delete(`document-editor:${documentId}`);
  }
}

/**
 * The tab on screen changed workspace without being re-activated: either the
 * user moved its document (Move to…, a drop on the switcher) — the tab goes
 * with the document and the workspace in view shows its next tab — or the tab
 * learnt where its document always lived (opened with a bare `{ type:
 * 'personal' }` by Graph / MCP, or re-located by followDocumentTab) — the view
 * follows it.
 */
function isCorrection(before, tab) {
  if (following.has(tab.id)) return true;
  const scope = before.scope || {};
  return (!scope.type || scope.type === 'personal') && scope.scopeId === undefined;
}

function onTabsChange(state, prev) {
  const tab = activeTab(state);
  if (!tab) return;
  const key = tabWorkspaceKey(tab);
  const ui = useDocumentsUiStore.getState?.();
  const current = normalizeWorkspaceKey(ui?.scopeKey);
  if (key !== current && prev && state.activeDocumentTabId === prev.activeDocumentTabId) {
    const before = activeTab(prev);
    if (before && tabWorkspaceKey(before) === current && !isCorrection(before, tab)) {
      lastActive = { ...lastActive, [key]: tab.id };
      setActiveDocumentTabId(tabToShowFor(state, current));
      return;
    }
  }
  lastActive = lastActive[key] === tab.id ? lastActive : { ...lastActive, [key]: tab.id };
  if (ui && current !== key) ui.setScopeKey(key);
}

function onScopeChange(scopeKey) {
  const key = normalizeWorkspaceKey(scopeKey);
  const state = useTabsStore.getState();
  const tab = activeTab(state);
  if (tab && tabWorkspaceKey(tab) === key) return;
  const next = tabToShowFor(state, key);
  if (next !== state.activeDocumentTabId) setActiveDocumentTabId(next);
}

/**
 * A tab that changed workspace (its document moved) joins the END of its new
 * workspace's strip, like a tab opened there — not wherever it sat in the
 * shared order.
 */
function moveArrivalsToEnd(state, prev) {
  const before = new Map(prev.documentTabs.map((tab) => [tab.id, tabWorkspaceKey(tab)]));
  const moved = new Set(
    state.documentTabs.filter((tab) => before.has(tab.id) && before.get(tab.id) !== tabWorkspaceKey(tab)).map((tab) => tab.id)
  );
  if (!moved.size) return;
  const order = state.tabStripOrder || [];
  const isMoved = (e) => e.type === 'document-editor' && moved.has(e.id);
  setTabStripOrder([...order.filter((e) => !isMoved(e)), ...order.filter(isMoved)]);
}

/** Wire both invariants (idempotent). Returns the uninstaller (tests). */
export function installDocumentTabWorkspaces() {
  if (installed || typeof useDocumentsUiStore.subscribe !== 'function') return () => {};
  installed = true;
  const offTabs = useTabsStore.subscribe((state, prev) => {
    if (state.activeDocumentTabId === prev.activeDocumentTabId && state.documentTabs === prev.documentTabs) return;
    if (state.documentTabs !== prev.documentTabs) moveArrivalsToEnd(state, prev);
    onTabsChange(state, prev);
  });
  const offScope = useDocumentsUiStore.subscribe((state, prev) => {
    if (state.scopeKey !== prev.scopeKey) onScopeChange(state.scopeKey);
  });
  return () => {
    offTabs();
    offScope();
    installed = false;
  };
}
